import { FC, useMemo } from "react";
import { Button, ConfigProvider, Modal } from "antd";
import dayjs from "dayjs";

import styles from "./index.less";

import { ReactComponent as PaySuccessIcon } from "../../assets/svg/pay-success.svg";
import QrCode from "../../assets/qrcode.png";

import { PlanPayModalTypeEnum } from "../../types";
import { getplatformUrl, getVersionById, isInPlatform } from "../../utils";
import { useVersionWithPrice } from "../../hooks";
import { IPaySuccessInformation } from "../../service";

import { PrimaryTheme } from "@packages/theme";

interface PlanPaySuccessModalProps {
  planId: string;
  type: PlanPayModalTypeEnum;
  open: boolean;
  onClose: () => void;
  paySuccessInformation?: IPaySuccessInformation;
}

/**
 * 支付和续费成功弹窗
 */
const PlanPaySuccessModal: FC<PlanPaySuccessModalProps> = ({
  planId,
  open,
  type,
  onClose,
  paySuccessInformation,
}) => {
  const { array: VersionWithPrice } = useVersionWithPrice();

  const versionInformation = getVersionById(planId, VersionWithPrice);

  const congratulationText = useMemo(() => {
    if (type === PlanPayModalTypeEnum.Renew) {
      return `恭喜！您已成功续费 12Times · ${versionInformation?.label} 版`;
    }

    return `恭喜！您已成功开通 12Times · ${versionInformation?.label} 版`;
  }, [type, versionInformation]);

  return (
    <ConfigProvider theme={PrimaryTheme}>
      <Modal
        centered
        maskClosable={false}
        open={open}
        width={580}
        footer={null}
        classNames={{
          content: styles["modal-content"],
        }}
        onClose={onClose}
        onCancel={onClose}
      >
        <div className={styles.container}>
          <PaySuccessIcon />
          <div className={styles.title}>
            {[
              PlanPayModalTypeEnum.Subscribe,
              PlanPayModalTypeEnum.Upgrade,
            ].includes(type)
              ? "支付成功"
              : "续费成功"}
          </div>
          <div className={styles.congratulation}>
            🎉
            <span className={styles.text}>{congratulationText}</span>
          </div>
          <div className={styles.information}>
            <div className={styles.left}>
              <div className={styles.item}>
                <div className={styles.label}>订单号：</div>
                <div className={styles.value}>
                  {paySuccessInformation?.orderId}
                </div>
              </div>
              <div className={styles.item}>
                <div className={styles.label}>支付金额：</div>
                <div className={styles.value}>
                  {paySuccessInformation?.payAmount}元（
                  {paySuccessInformation?.goodsName}）
                </div>
              </div>

              {[
                PlanPayModalTypeEnum.Subscribe,
                PlanPayModalTypeEnum.Upgrade,
              ].includes(type) ? (
                <>
                  <div className={styles.item}>
                    <div className={styles.label}>服务生效日期：</div>
                    <div className={styles.value}>
                      {dayjs(paySuccessInformation?.startTime).format(
                        "YYYY-MM-DD"
                      )}
                    </div>
                  </div>
                  <div className={styles.item}>
                    <div className={styles.label}>服务截止日期：</div>
                    <div className={styles.value}>
                      {dayjs(paySuccessInformation?.endTime)
                        .subtract(1, "day")
                        .format("YYYY-MM-DD")}
                    </div>
                  </div>
                </>
              ) : (
                <div className={styles.item}>
                  <div className={styles.label}>服务截止日期延长至：</div>
                  <div className={styles.value}>
                    {dayjs(paySuccessInformation?.endTime)
                      .subtract(1, "day")
                      .format("YYYY-MM-DD")}
                  </div>
                </div>
              )}

              <div className={styles.item}>
                <div className={styles.label}>发票信息：</div>
                <div className={styles.value}>扫一扫右侧二维码添加客服开票</div>
              </div>
            </div>
            <div className={styles["img-box"]}>
              <img src={QrCode} alt="二维码" className={styles.img} />
            </div>
          </div>

          <Button
            className={styles.redirect}
            type="primary"
            onClick={() => {
              if (!isInPlatform()) {
                window.location.href = getplatformUrl();
                return;
              }

              onClose();
              // 清除history state
              window.history.replaceState({}, "", window.location.pathname);
              window.location.reload();
            }}
          >
            {!isInPlatform() ? "前往12Times" : "我知道了"}
          </Button>
        </div>
      </Modal>
    </ConfigProvider>
  );
};

export default PlanPaySuccessModal;
